<template>
  <div>
    <div class="row">
      <div class="col-9 d-flex flex-column gap-1">
        <div :class="positionClass">{{ approverPosition }}</div>
        <span class="approval-description" v-html="approvalHistoryDescriptionHtml"/>
        <template v-if="step.history">
          <span :class="eventAtClass">{{ formattedEventAt }}</span>
          <b-link
              v-if="approvalHistoryAttachmentUrl(step.history.file_url)"
              :href="approvalHistoryAttachmentUrl(step.history.file_url)"
              class="approval-attachment-link"
              rel="noopener noreferrer"
              target="_blank"
          >
            {{ lodash.startCase($t('sales_plans.approval_histories.see_attachment')) }}
          </b-link>
          <b-button
              v-if="step.status === 'cancelled' && historyReason"
              variant="link"
              class="approval-attachment-link approval-reason-toggle"
              :aria-expanded="cancellationReasonVisible"
              :aria-controls="cancellationReasonCollapseId"
              @click="cancellationReasonVisible = !cancellationReasonVisible"
          >
            {{ lodash.startCase($t(cancellationReasonVisible ? 'sales_plans.approval_histories.hide_reason' : 'sales_plans.approval_histories.see_reason')) }}
          </b-button>
        </template>
      </div>
      <div class="col-3 d-flex align-items-center justify-content-end">
        <status-badge
            v-if="step.status"
            :status="step.status"
            entity="sales_plan"
            type="approval_status"
        ></status-badge>
        <span v-else>-</span>
      </div>
      <div v-if="historyReason && step.status !== 'cancelled'" class="col-12">
        <p :class="['step-reason mt-1 mb-0', {
          'step-reason--approved': step.status === 'approved',
          'step-reason--rejected': step.status === 'rejected',
        }]">"{{ historyReason }}"</p>
      </div>
      <div v-if="historyReason && step.status === 'cancelled'" class="col-12">
        <b-collapse :id="cancellationReasonCollapseId" v-model="cancellationReasonVisible">
          <p class="step-reason mt-1 mb-0">"{{ historyReason }}"</p>
          <div v-if="cancellationPerformer" class="approval-description mt-2">
            {{ lodash.upperFirst($t('sales_plans.cancelled_by')) }}: <b>{{ cancellationPerformer }}</b>
          </div>
        </b-collapse>
      </div>
    </div>
    <hr v-if="!isLast"/>
  </div>
</template>

<script>
import StatusBadge from "@/components/badges/status-badge.vue";

const stepTextVariants = {
  pending: {
    positionClass: 'approver-position approver-position--default',
    eventAtClass: 'approval-event-at approval-event-at--muted',
  },
  approved: {
    positionClass: 'approver-position approver-position--default',
    eventAtClass: 'approval-event-at approval-event-at--muted',
  },
  rejected: {
    positionClass: 'approver-position approver-position--danger',
    eventAtClass: 'approval-event-at approval-event-at--danger',
  },
  waiting: {
    positionClass: 'approver-position approver-position--waiting',
    eventAtClass: 'approval-event-at approval-event-at--muted',
  },
  cancelled: {
    positionClass: 'approver-position approver-position--default',
    eventAtClass: 'approval-event-at approval-event-at--muted',
  },
}

export default {
  name: 'sales-plan-approval-progress-step',
  components: {
    StatusBadge,
  },
  data() {
    return {
      cancellationReasonVisible: false,
    }
  },
  props: {
    step: {
      type: Object,
      required: true,
    },
    isLast: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    cancellationReasonCollapseId() {
      return `sales-plan-cancellation-reason-${this._uid}`
    },
    approver() {
      if (this.step.status === 'cancelled') {
        return this.step.template_approver ?? this.step.approver
      }

      return ['current', 'pending'].includes(this.step.status) ? this.step.template_approver : this.step.approver
    },
    approverPosition() {
      return this.extractApproverPosition(this.approver)
    },
    approvalHistoryDescriptionHtml() {
      if (this.step.status === 'cancelled') {
        const approverLabel = this.lodash.upperFirst(this.$t('sales_plans.approver'))
        const approverName = this.lodash.escape(this.approver?.name ?? '-')

        return `${approverLabel}: <b>${approverName}</b>`
      }

      const descriptionKeyByStatus = {
        approved: 'sales_plans.approved_by',
        rejected: 'sales_plans.rejected_by',
      }
      const descriptionKey = descriptionKeyByStatus[this.step.status] || 'sales_plans.waiting_for_approval_by'
      const beforeName = this.lodash.upperFirst(this.$t(descriptionKey))
      const approverName = this.lodash.escape(this.approver?.name ?? '-')

      return `${beforeName}: <b>${approverName}</b>`
    },
    statusVariant() {
      return stepTextVariants[this.step.status] || stepTextVariants.pending
    },
    positionClass() {
      return this.statusVariant.positionClass
    },
    eventAtClass() {
      return this.statusVariant.eventAtClass
    },
    formattedEventAt() {
      return this.step.history ? this.$moment(this.step.history.event_at).format('DD-MM-YYYY') : ''
    },
    historyReason() {
      return this.step.history?.reason || null
    },
    cancellationPerformer() {
      return this.step.history?.performed_by?.name || null
    },
  },
  methods: {
    approvalHistoryAttachmentUrl(filePath) {
      if (!filePath) {
        return null
      }

      return encodeURI(filePath)
    },
    extractApproverPosition(approver) {
      return approver?.position
    },
  },
}
</script>

<style lang="scss" scoped>
.approver-position {
  font: 600 12px/14px Inter;

  &--default {
    color: #171717;
  }

  &--danger {
    color: #DC2626;
  }

  &--waiting {
    color: #DC2626;
  }
}

.approval-event-at {
  font: 400 10px/12px Inter;

  &--muted {
    color: #6C7589;
  }

  &--danger {
    color: #F87171;
  }
}

.approval-description {
  font: 400 10px/12px Inter;
  color: #98A2B3;
  font-style: italic;
}

.approval-attachment-link {
  font: 700 10px/14px Inter;
  color: #1664D8;
}

.approval-reason-toggle {
  align-self: flex-start;
  padding: 0;
  text-align: left;
}

.step-reason {
  border-radius: 8px;
  font: 400 10px/12px Inter;
  padding: 8px;
  background-color: #F3F4F6;
  border: 1px solid #E5E7EB;
  color: #111827;
}

.step-reason--approved {
  background-color: #ECFDF3;
  border: 1px solid #A7F3D0;
  color: #047857;
}

.step-reason--rejected {
  background-color: #FEE2E2;
  border: 1px solid #F87171;
  color: #DC2626;
}
</style>
